"use client";

import { ArrowRight, Star } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  ALL_SERVICES,
  type CityCompanyListItem,
  serviceLabel,
} from "@/features/companies/shared/constants";
import { companyInitials } from "@/features/companies/shared/name";
import { formatCompactCount, formatDecimal } from "@/shared/format/number";

const PAGE_SIZE = 12;

type Props = {
  citySlug: string;
  // SSR-rendered anchor fallback. Shown while the client fetch is in flight;
  // ensures crawlers find company links without blocking pageload on the DB.
  children: ReactNode;
};

export function CityCompanyGrid({ citySlug, children }: Props) {
  const [companies, setCompanies] = useState<CityCompanyListItem[] | null>(null);
  const [serviceFilter, setServiceFilter] = useState<string>("Alle");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/firmaer/by-city/${citySlug}`)
      .then((r) => r.json())
      .then((data: { ok: boolean; companies?: CityCompanyListItem[] }) => {
        if (!cancelled && data.ok && data.companies) {
          setCompanies(data.companies);
        }
      })
      .catch(() => {
        // Silent failure - the anchor list stays visible as a fallback
      });
    return () => {
      cancelled = true;
    };
  }, [citySlug]);

  const filtered = useMemo(() => {
    if (!companies) return [];
    let result = companies;

    if (serviceFilter !== "Alle") {
      result = result.filter((c) => c.services.includes(serviceFilter));
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (c) => c.name.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q),
      );
    }

    return result;
  }, [companies, serviceFilter, search]);

  // Before the client fetch finishes: show the SSR-rendered anchor list.
  if (companies === null) {
    return <>{children}</>;
  }

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <>
      {/* Search */}
      <div className="max-w-100">
        <input
          type="text"
          placeholder="Søg efter firmanavn..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisibleCount(PAGE_SIZE);
          }}
          className="w-full rounded-lg bg-white px-4 py-2.5 text-sm text-ink outline-none transition-colors focus:ring-2 focus:ring-link"
        />
      </div>

      {/* Service pills */}
      <div className="mt-4 flex flex-wrap gap-2">
        {["Alle", ...ALL_SERVICES.slice(0, 6)].map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => {
              setServiceFilter(s);
              setVisibleCount(PAGE_SIZE);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              serviceFilter === s
                ? "bg-theme text-white"
                : "bg-white text-ink-muted hover:bg-surface-hover"
            }`}
          >
            {serviceLabel(s)}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="mt-6 text-sm text-ink-subtle">
        {filtered.length} firma{filtered.length !== 1 ? "er" : ""}
      </p>

      {/* Grid */}
      <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((company) => (
          <Link
            key={company.slug}
            href={`/firmaer/${company.slug}`}
            className="group flex flex-col rounded-lg bg-white p-6 hover:bg-surface"
          >
            {/* Header: logo/avatar + name */}
            <div className="flex items-center gap-3">
              {company.logoUrl ? (
                // biome-ignore lint/performance/noImgElement: Doesnt matter
                <img
                  src={company.logoUrl}
                  alt={`${company.name} logo`}
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 shrink-0 rounded-full object-contain"
                />
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-theme text-sm font-bold text-white">
                  {companyInitials(company.name)}
                </span>
              )}
              <div>
                <h3 className="font-serif text-lg font-bold text-ink group-hover:underline">
                  {company.name}
                </h3>
                {company.city && <p className="text-xs text-ink-subtle">{company.city}</p>}
              </div>
            </div>

            {/* Service pills */}
            <div className="mt-4 flex flex-wrap gap-1.5">
              {company.services.slice(0, 3).map((svc) => (
                <span
                  key={svc}
                  className="rounded-full bg-surface px-2.5 py-0.5 text-xs text-ink-subtle"
                >
                  {serviceLabel(svc)}
                </span>
              ))}
              {company.services.length > 3 && (
                <span className="rounded-full bg-surface px-2.5 py-0.5 text-xs text-ink-subtle">
                  +{company.services.length - 3}
                </span>
              )}
            </div>

            {/* Rating + link */}
            <div className="mt-auto flex items-center justify-between pt-5">
              <div className="flex items-center gap-3">
                {company.googleRating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-[#FBBC04]" aria-hidden="true" />
                    <span className="text-sm font-bold text-ink">
                      {formatDecimal(company.googleRating)}
                    </span>
                    {company.googleReviews && (
                      <span className="text-xs text-ink-subtle">
                        ({formatCompactCount(company.googleReviews)})
                      </span>
                    )}
                  </div>
                )}
                {company.trustpilotScore && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-[#00B67A]">
                      {formatDecimal(company.trustpilotScore)}
                    </span>
                    <span className="text-xs text-ink-subtle">TP</span>
                  </div>
                )}
              </div>

              <span className="flex items-center gap-1.5 text-sm font-bold text-link">
                Læs mere
                <ArrowRight className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden="true" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            className="rounded-lg bg-white px-6 py-2.5 text-sm font-medium text-link transition-colors hover:bg-surface-hover"
          >
            Vis flere ({filtered.length - visibleCount} resterende)
          </button>
        </div>
      )}
    </>
  );
}
